// ENG-057: how tall the avatar menu may be. It used to be `calc(100vh - 96px)`, which is 0 in a
// WebView shorter than 96px (a bottom sheet / dialog) — the menu then opened as a 2px "white
// line" — and which ignored that the chat panel clips the menu long before the window does.
// So the height comes from the space that is really there when the menu opens.

/** Below this the menu stops shrinking: it overflows and scrolls instead of becoming a line. */
export const PROFILE_MENU_MIN_HEIGHT = 160;
/** Air kept between the menu and the edge that ends the available space. */
export const PROFILE_MENU_EDGE_GAP = 12;

export interface ProfileMenuLimits {
    /** Bottom edges (viewport px) of the menu's ancestors that clip it (overflow other than visible). */
    clipBottoms: number[];
    /** `window.innerHeight`; a WebView can report 0 while it lays out — then it is ignored. */
    innerHeight: number;
    /** Bottom of the `visualViewport` (offsetTop + height); ignored when not above 0. */
    visualViewportBottom: number;
}

const isUsable = (value: number): boolean => Number.isFinite(value) && value > 0;

/** The `max-height` in px for a menu whose top edge is at `menuTop`, or null when nothing bounds it. */
export const profileMenuMaxHeight = (menuTop: number, limits: ProfileMenuLimits): number | null => {
    const bounds = [...limits.clipBottoms.filter(Number.isFinite), ...[limits.innerHeight, limits.visualViewportBottom].filter(isUsable)];
    if (!bounds.length) {
        return null;
    }
    const available = Math.min(...bounds) - menuTop - PROFILE_MENU_EDGE_GAP;
    return Math.max(PROFILE_MENU_MIN_HEIGHT, Math.round(available));
};

/** Reads the real limits for `menu` from the DOM (bottom of every clipping ancestor, the window, the visual viewport). */
export const readProfileMenuLimits = (menu: HTMLElement): ProfileMenuLimits => {
    const clipBottoms: number[] = [];
    // html and body are left out: their overflow is handed to the viewport, and their box can be
    // shorter than the screen (the window is what bounds the menu there).
    for (let el = menu.parentElement; el && el !== document.body && el !== document.documentElement; el = el.parentElement) {
        const style = getComputedStyle(el);
        if (style.overflow !== "visible" || style.overflowY !== "visible") {
            clipBottoms.push(el.getBoundingClientRect().bottom);
        }
    }
    const visual = window.visualViewport;
    return {
        clipBottoms,
        innerHeight: window.innerHeight,
        visualViewportBottom: visual ? visual.offsetTop + visual.height : 0
    };
};
